import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Shield, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const Payment = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isPaying, setIsPaying] = useState(false);
  const [applicationData, setApplicationData] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);

  // Function to get the display text for course type
  const getCourseTypeDisplay = (courseType: string) => {
    switch(courseType) {
      case 'call':
        return 'Voice/Video Call';
      case 'voiceCall':
        return 'Voice/Video Call';
      case 'subscription':
        return 'Subscription';
      default:
        return courseType;
    }
  };

  useEffect(() => {
    const fetchApplicationData = async () => {
      console.log("=== PAYMENT PAGE DEBUG ===");
      console.log("Auth loading:", authLoading);
      console.log("User:", user);
      console.log("Application ID:", applicationId);
      
      // Wait for auth to be ready
      if (authLoading) {
        console.log("Still loading auth, waiting...");
        return;
      }
      
      if (!applicationId) {
        console.log("No application ID provided");
        return;
      }
      
      setIsLoading(true);
      
      try {
        // Check if user is authenticated
        if (!user) {
          console.log("No authenticated user - redirecting to login");
          toast({
            title: "Authentication Required",
            description: "Please log in to complete your payment.",
            variant: "destructive",
          });
          navigate('/login', { replace: true });
          return;
        }

        console.log("User authenticated, fetching application data...");

        // First, fetch the application data
        const { data: appData, error: appError } = await supabase
          .from('applications')
          .select(`
            id, 
            user_id, 
            mentor_id, 
            status, 
            course_id, 
            payment_amount, 
            is_subscription_active, 
            payment_status, 
            subscription_start_date
          `)
          .eq('id', applicationId)
          .single();
          
        if (appError) {
          console.error("Error fetching application:", appError);
          throw new Error(`Application query failed: ${appError.message}`);
        }
        
        if (!appData) {
          console.log("Application not found");
          toast({
            title: "Error",
            description: "Application not found.",
            variant: "destructive",
          });
          navigate('/mentee-dashboard', { replace: true });
          return;
        }

        console.log("Application data retrieved:", appData);

        // Verify user owns this application
        if (appData.user_id !== user.id) {
          console.log("User does not own this application");
          toast({
            title: "Access Denied",
            description: "You don't have permission to access this application.",
            variant: "destructive",
          });
          navigate('/mentee-dashboard', { replace: true });
          return;
        }

        // Fetch related data
        console.log("Fetching course data...");
        const { data: courseData, error: courseError } = await supabase
          .from('courses')
          .select(`
            id,
            title,
            price,
            course_type,
            weekly_call_allowance,
            included_sessions
          `)
          .eq('id', appData.course_id)
          .single();

        if (courseError || !courseData) {
          console.error("Error fetching course:", courseError);
          throw new Error("Course not found");
        }

        console.log("Fetching mentor profile...");
        const { data: mentorData, error: mentorError } = await supabase
          .from('profiles')
          .select(`
            first_name,
            last_name
          `)
          .eq('id', appData.mentor_id)
          .single();

        if (mentorError || !mentorData) {
          console.error("Error fetching mentor profile:", mentorError);
          throw new Error("Mentor profile not found");
        }

        // Combine all data
        const combinedData = {
          ...appData,
          courses: courseData,
          profiles: mentorData
        };

        console.log("Combined application data:", combinedData);
        
        // Check if already paid/active
        const isPaid = combinedData.status === 'paid';
        const isSubscriptionActive = combinedData.is_subscription_active === true;
        
        if (isPaid || isSubscriptionActive) {
          console.log("Application is already paid/active - redirecting");
          
          const message = isSubscriptionActive ? 
            "Your subscription is already active!" : 
            "Your payment has been completed!";
          
          const title = isSubscriptionActive ? 
            "Subscription Active" : 
            "Payment Completed";
          
          toast({
            title,
            description: message,
          });
          
          navigate('/mentee-dashboard', { replace: true });
          return;
        }
        
        // Check if payment is required
        if (combinedData.status !== 'pending_mentee_purchase') {
          let message = "This application is not awaiting payment.";
          let title = "Payment Not Required";
          
          if (combinedData.status === 'completed') {
            message = "This application has been completed.";
            title = "Application Completed";
          } else if (combinedData.status === 'rejected') {
            message = "This application has been rejected.";
            title = "Application Rejected";
          }
          
          console.log("Application not awaiting payment:", combinedData.status);
          toast({
            title: title,
            description: message,
          });
          navigate('/mentee-dashboard', { replace: true });
          return;
        }
        
        console.log("Application ready for payment");
        setApplicationData(combinedData);
      } catch (error) {
        console.error("=== PAYMENT PAGE ERROR ===");
        console.error("Error details:", error);
        
        toast({
          title: "Error",
          description: `Failed to load payment details: ${error.message || 'Unknown error'}`,
          variant: "destructive",
        });
        navigate('/mentee-dashboard', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchApplicationData();
  }, [applicationId, navigate, toast, user, authLoading]);

  const handleTestPayment = async () => {
    setIsTesting(true);
    try {
      console.log("Testing payment function...");
      const { data, error } = await supabase.functions.invoke('test-payment-function', {
        body: {}
      });
      
      console.log("Test function response:", { data, error });
      
      if (error) {
        throw new Error(`Test failed: ${error.message}`);
      }
      
      toast({
        title: "Test Completed",
        description: "Check the browser console and edge function logs for detailed results.",
      });
    } catch (error) {
      console.error("Test function error:", error);
      toast({
        title: "Test Failed",
        description: `Test error: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleMakePayment = async () => {
    if (!applicationData || !applicationId || !user) {
      console.error("Missing required data for payment", {
        hasApplicationData: !!applicationData,
        hasApplicationId: !!applicationId,
        hasUser: !!user
      });
      toast({
        title: "Error",
        description: "Missing required information for payment.",
        variant: "destructive",
      });
      return;
    }
    
    console.log("=== STARTING PAYMENT PROCESS ===");
    console.log("Application ID:", applicationId);
    console.log("Course type:", applicationData.courses.course_type);
    console.log("User ID:", user.id);
    
    setIsPaying(true);
    
    try {
      console.log("Getting current session...");
      const { data: sessionData } = await supabase.auth.getSession();
      console.log("Session data:", sessionData.session ? "Present" : "Missing");
      
      if (!sessionData.session) {
        throw new Error("No active session found. Please log in again.");
      }

      console.log("Calling create-checkout-session function...");
      
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { applicationId: applicationData.id }
      });
      
      console.log("Edge function response:", { data, error });
      
      if (error) {
        console.error("Edge function error:", error);
        throw new Error(`Payment service error: ${error.message}`);
      }
      
      if (!data || !data.success) {
        const errorMessage = data?.error || "Failed to create checkout session";
        console.error("Checkout session creation failed:", errorMessage);
        throw new Error(errorMessage);
      }
      
      if (!data.checkoutUrl) {
        console.error("No checkout URL received");
        throw new Error("No payment URL received from payment processor");
      }
      
      console.log("Opening Stripe checkout in new tab");
      
      // Always open payment in new tab
      const paymentWindow = window.open(data.checkoutUrl, '_blank');
      
      if (paymentWindow) {
        toast({
          title: "Payment Window Opened",  
          description: "Complete your payment in the new tab that just opened.",
        });
      } else {
        // Popup blocked - show manual link
        toast({
          title: "Popup Blocked",
          description: "Please allow popups and try again, or copy the payment link.",
          variant: "destructive",
        });
        
        // Also try to copy to clipboard
        try {
          await navigator.clipboard.writeText(data.checkoutUrl);
          console.log("Payment URL copied to clipboard");
        } catch (e) {
          console.log("Could not copy to clipboard");
        }
      }
      
    } catch (error) {
      console.error("=== PAYMENT INITIATION ERROR ===");
      console.error("Payment error:", error);
      
      const errorMessage = error.message || 'Unknown error occurred during payment initiation';
      
      toast({
        title: "Payment Failed",
        description: `Failed to initiate payment: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsPaying(false);
    }
  };

  // Show loading state
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Navbar />
        
        <main className="flex-grow pt-8 pb-12 flex items-center justify-center">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-8 h-8 border-2 border-emu-blue border-t-transparent rounded-full animate-spin" />
                <p className="text-lg text-gray-600">
                  {authLoading ? "Checking authentication..." : "Loading payment details..."}
                </p>
              </div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      {/* Page Header Banner */}
      <div className="w-full bg-cover bg-center h-64 relative" style={{ backgroundImage: "url('/lovable-uploads/63560c3c-d7c3-4d91-b852-d2cd79229bf8.png')" }}>
        <div className="absolute inset-0 bg-emu-blue/20"></div>
        <div className="container mx-auto px-4 h-full flex items-end pb-8 relative z-10">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Complete Your Payment
            </h1>
            <p className="text-gray-700 mt-2">
              Secure checkout powered by Stripe
            </p>
          </div>
        </div>
      </div>
      
      <main className="flex-grow pt-12 pb-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/mentee-dashboard')}
              className="mb-8 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
            </Button>
            
            {applicationData ? (
              <Card className="p-8 shadow-lg border-0 bg-white">
                <div className="space-y-8">
                  {/* Payment Details Header */}
                  <div className="text-center">
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">Payment Details</h2>
                    <p className="text-gray-600">Complete payment to start your mentorship journey</p>
                  </div>
                  
                  {/* Payment Details Grid */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="grid gap-4">
                      <div className="grid grid-cols-2 gap-4 py-3 border-b border-gray-200 last:border-b-0">
                        <span className="text-sm font-medium text-gray-600">Course</span>
                        <span className="font-semibold text-gray-900 text-right">{applicationData.courses.title}</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 py-3 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">Type</span>
                        <span className="font-semibold text-gray-900 text-right">{getCourseTypeDisplay(applicationData.courses.course_type)}</span>
                      </div>
                      
                      {applicationData.courses.included_sessions && applicationData.courses.included_sessions > 1 && (
                        <div className="grid grid-cols-2 gap-4 py-3 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Included Sessions</span>
                          <span className="font-semibold text-gray-900 text-right">{applicationData.courses.included_sessions}</span>
                        </div>
                      )}
                      
                      {applicationData.courses.weekly_call_allowance && (
                        <div className="grid grid-cols-2 gap-4 py-3 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">Weekly Calls</span>
                          <span className="font-semibold text-gray-900 text-right">{applicationData.courses.weekly_call_allowance}</span>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4 py-3 border-b border-gray-200">
                        <span className="text-sm font-medium text-gray-600">Mentor</span>
                        <span className="font-semibold text-gray-900 text-right">{`${applicationData.profiles.first_name} ${applicationData.profiles.last_name}`}</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 py-4 border-t-2 border-gray-300 mt-2">
                        <span className="text-lg font-bold text-gray-900">Total</span>
                        <span className="text-xl font-bold text-emu-blue text-right">A${applicationData.courses.price}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment Button */}
                  <div className="space-y-6">
                    <Button
                      onClick={handleMakePayment}
                      className="w-full py-4 bg-emu-blue hover:bg-emu-blue/90 transition-all duration-200 transform hover:scale-[1.02] shadow-md hover:shadow-lg"
                      disabled={isPaying}
                      size="lg"
                    >
                      {isPaying ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3" />
                          Opening Payment...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <Lock size={18} className="mr-3" />
                          Secure Payment • Proceed to Checkout
                        </div>
                      )}
                    </Button>
                    
                    {/* Security & Trust Indicators */}
                    <div className="space-y-4 text-center">
                      <div className="flex items-center justify-center text-sm text-gray-600">
                        <Shield size={16} className="mr-2 text-green-600" />
                        Your payment is securely processed via Stripe
                      </div>
                      
                      <div className="flex items-center justify-center space-x-6 text-xs text-gray-500">
                        <div className="flex items-center space-x-2">
                          <CreditCard size={14} />
                          <span>Stripe</span>
                        </div>
                        <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded">VISA</div>
                        <div className="px-2 py-1 bg-red-100 text-red-800 rounded">MC</div>
                        <div className="px-2 py-1 bg-green-100 text-green-800 rounded">AMEX</div>
                      </div>
                      
                      <p className="text-xs text-gray-500">
                        <Lock size={12} className="inline mr-1" />
                        We never store your card details
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ) : (
              <div className="text-center p-8">
                <p className="text-gray-600 mb-4">Application not found or payment not required.</p>
                <Button 
                  onClick={() => navigate('/mentee-dashboard')} 
                  className="bg-emu-blue hover:bg-emu-blue/90"
                >
                  Return to Dashboard
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Payment;
